import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { toast } from 'sonner';
import { DialogoConfirmar } from './ui/confirmar';
import { Plus, Trash2, Star, Pencil, Loader2, PenLine } from 'lucide-react';
import SignatureEditor from './SignatureEditor';

export default function SignatureManager({ accounts = [] }) {
  const qc = useQueryClient();
  const { data: signatures = [] } = useQuery({ queryKey: ['signatures'], queryFn: async () => (await api.get('/signatures')).data });
  const [editing, setEditing] = useState(null);
  const [borrarId, setBorrarId] = useState(null);
  const [saving, setSaving] = useState(false);

  const accountLabel = (aid) => (aid ? (accounts.find((a) => a.id === aid)?.email || 'Cuenta') : 'Todas las cuentas');

  const startNew = () => setEditing({ name: '', html: '', account_id: null, is_default: signatures.length === 0 });
  const startEdit = (s) => setEditing({ ...s });

  const save = async () => {
    if (!editing.name.trim()) { toast.error('Ponle un nombre a la firma'); return; }
    setSaving(true);
    try {
      const payload = { name: editing.name.trim(), html: editing.html || '', account_id: editing.account_id || null, is_default: !!editing.is_default };
      if (editing.id) await api.put(`/signatures/${editing.id}`, payload);
      else await api.post('/signatures', payload);
      qc.invalidateQueries({ queryKey: ['signatures'] });
      setEditing(null);
      toast.success('Firma guardada');
    } catch { toast.error('Error al guardar la firma'); }
    finally { setSaving(false); }
  };

  const del = (id) => setBorrarId(id);   // diálogo propio en vez de window.confirm (M27)
  const borrar = async (id) => {
    try { await api.delete(`/signatures/${id}`); qc.invalidateQueries({ queryKey: ['signatures'] }); toast.success('Firma eliminada'); }
    catch { toast.error('No se pudo eliminar'); }
  };

  if (editing) {
    return (
      <div className="space-y-3" data-testid="signature-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Nombre de la firma</Label>
            <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Ej. Formal, Breve, Interna" data-testid="signature-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Cuenta asociada</Label>
            <Select value={editing.account_id || 'all'} onValueChange={(v) => setEditing({ ...editing, account_id: v === 'all' ? null : v })}>
              <SelectTrigger data-testid="signature-account"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las cuentas</SelectItem>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <SignatureEditor value={editing.html} onChange={(html) => setEditing((e) => ({ ...e, html }))} />
        <div className="flex items-center justify-between pt-1">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch checked={!!editing.is_default} onCheckedChange={(v) => setEditing({ ...editing, is_default: v })} data-testid="signature-default" />
            Predeterminada para esta cuenta
          </label>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => setEditing(null)} data-testid="signature-cancel">Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="signature-save">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Guardar firma</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="signature-list">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Crea varias firmas y asígnalas a tus cuentas. En el redactor podrás elegir cuál usar.</div>
        <Button size="sm" className="gap-1.5 shrink-0" onClick={startNew} data-testid="signature-add"><Plus className="h-4 w-4" />Nueva firma</Button>
      </div>
      {signatures.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-xl">Aún no tienes firmas. Crea la primera.</div>
      ) : (
        <div className="space-y-2">
          {signatures.map((s) => (
            <div key={s.id} className="flex items-center gap-3 p-3 rounded-xl border border-border" data-testid="signature-row">
              <PenLine className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-2">
                  {s.name}
                  {s.is_default && <span className="inline-flex items-center gap-1 text-[11px] text-primary"><Star className="h-3 w-3 fill-primary" />Predeterminada</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">{accountLabel(s.account_id)}</div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(s)} data-testid="signature-edit" aria-label="Editar firma" title="Editar firma"><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(s.id)} data-testid="signature-delete" aria-label="Eliminar firma" title="Eliminar firma"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}
      <DialogoConfirmar
        open={!!borrarId}
        titulo="¿Eliminar esta firma?"
        mensaje="Dejará de añadirse a tus correos. Los que ya enviaste no cambian."
        boton="Eliminar firma"
        testId="signature-confirmar-borrar"
        onOpenChange={(v) => !v && setBorrarId(null)}
        onConfirmar={() => { const id = borrarId; setBorrarId(null); if (id) borrar(id); }}
      />
    </div>
  );
}
